"use client";

import React, { useEffect, useMemo } from "react";
import { Menu } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Avatar } from "../components/ui/avatar";
import { Link, Outlet, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import Logout from "./Icon/Logout";
import useAuth from "../hooks/useAuth";
import useNav from "../hooks/useNav";
import { message } from "antd";

function MainLayout() {
  const navigate = useNavigate();
  const { logOut, user } = useAuth();
  const { navItems, location, isActiveNav } = useNav();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const avatarUrl = `https://robohash.org/${Math.random()}.png`;

  useEffect(() => {
    const accessibleNav = navItems.map((nav) => nav.href);
    const found = accessibleNav.find((nav) => location.pathname.startsWith(nav))

    if (!found) {
      if (accessibleNav.length > 0) {
        navigate(accessibleNav[0])
      }
      // else {
      //   message.error("No accessible navigation items. Please contact administrator.")
      //   navigate("/login")
      // }
    }
  }, [])

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Main Content Area */}
      {/* Top Header */}
      <header className="h-16 border-b flex items-center justify-between px-4 bg-primary-accent">
        <Link to={"/dashboard"} className="hidden md:block">
          <img src={logo} alt="Godump Logo" width={100} />
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <Avatar>
            <img src={avatarUrl} alt={user?.name} />
          </Avatar>
          <div className="text-right">
            <div className="font-medium">{user?.name}</div>
            <div className="text-xs text-blue-600">Administrator</div>
          </div>
        </div>
      </header>
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Desktop */}
        <aside className="hidden md:flex flex-col w-16 bg-black text-white">
          <nav className="flex-1 flex flex-col items-center gap-6 py-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-md",
                    isActiveNav(item.href)
                      ? "bg-blue-600 text-white"
                      : "text-gray-400 hover:text-white"
                  )}
                  title={item.label}
                >
                  <Icon size={20} />
                </Link>
              );
            })}
          </nav>
          <div className="p-4 mt-auto">
            <Button
            onClick={() => logOut()}
              size="icon"
              className="text-gray-400 hover:text-white bg-transparent"
            >
              <Logout/>
            </Button>
          </div>
        </aside>

        {/* Mobile Menu Button */}
        <div className="md:hidden fixed top-0 left-0 z-50 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-black"
          >
            <Menu size={24} />
          </Button>
        </div>

        {/* Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/5"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <aside className="w-64 h-full bg-black text-white p-4">
              <div className="mb-6">
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="no-underline"
                >
                  <div className="flex items-center">
                    <span className="font-bold text-blue-500">Go</span>
                    <span className="font-bold text-white">Dump</span>
                  </div>
                </Link>
              </div>
              <nav className="flex flex-col gap-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2 rounded-md !no-underline",
                        isActiveNav(item.href)
                          ? "bg-blue-600 text-white"
                          : "text-gray-400 hover:text-white"
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span className="capitalize">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-primary-accent px-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default MainLayout;